import Phaser from "phaser";
import { LootBox } from "../models/loot-box";
import { MAP_HEIGHT_TILES, MAP_WIDTH_TILES, TILE_SIZE } from "../models/common";

export const TELEPORT_TILE_INDEX = [213, 1557, 64]; // Tile object index
const CHEST_TILE_INDEX = 2108; // Tile object index for chest containers (mirrors server/src/handlers/map-handler.ts)

// Default fallback values - actual values are obtained from loaded map

export interface TilesetConfig {
  name: string;
  imageKey: string;
  imagePath: string;
  extruded?: boolean; // If true, will use extruded version and apply margin/spacing
  margin?: number; // Margin pixels for extruded tilesets
  spacing?: number; // Spacing pixels for extruded tilesets
}

export interface MapHandlerConfig {
  mapKey: string;
  mapJSONPath: string;
  tilesets: TilesetConfig[];
}

export class MapHandler {
  private scene: Phaser.Scene;
  private config: MapHandlerConfig;
  public map: Phaser.Tilemaps.Tilemap | null = null;
  public layers: Record<string, Phaser.Tilemaps.TilemapLayer> = {};
  public lootBoxes: LootBox[] = [];
  public teleportSprites: Phaser.GameObjects.Sprite[] = [];
  public teleportsActive: Array<{ name: string; x: number; y: number }> = [];
  private fogOverlayContainer?: Phaser.GameObjects.Container;
  private fogTiles: Phaser.GameObjects.Graphics[] = [];
  private fogGraphics?: Phaser.GameObjects.Graphics;

  constructor(scene: Phaser.Scene, config: MapHandlerConfig) {
    this.scene = scene;
    this.config = config;
  }

  preload() {
    this.scene.load.tilemapTiledJSON(
      this.config.mapKey,
      this.config.mapJSONPath,
    );
    this.config.tilesets.forEach((ts) => {
      // Load extruded version if specified, otherwise load original
      const imagePath = ts.extruded
        ? ts.imagePath.replace(".png", "-extruded.png")
        : ts.imagePath;
      this.scene.load.image(ts.imageKey, imagePath);
    });
    // Ensure teleport-active is loaded as spritesheet (2 frames, 96x96 each)
    this.scene.load.spritesheet(
      "teleport-active",
      "/sprites/map/teleport-active.png",
      { frameWidth: 96, frameHeight: 96 },
    );
  }

  create() {
    if (!this.config || !this.config.tilesets) {
      console.error(
        "[MapHandler] Config or tilesets not properly initialized.",
        this.config,
      );
      return;
    }

    this.map = this.scene.make.tilemap({ key: this.config.mapKey });
    if (!this.map) {
      console.error(
        "[MapHandler] Tilemap could not be created. Check mapKey and mapJSONPath.",
      );
      return;
    }

    // Add all tilesets
    const tilesetObjects: Phaser.Tilemaps.Tileset[] = [];
    this.config.tilesets.forEach((ts) => {
      if (!this.map) {
        console.error("[MapHandler] Tilemap is null when adding tilesets");
        return;
      }

      // Use margin and spacing if tileset is extruded
      const margin = ts.extruded ? (ts.margin ?? 1) : 0;
      const spacing = ts.extruded ? (ts.spacing ?? 2) : 0;

      const tileset = this.map.addTilesetImage(
        ts.name,
        ts.imageKey,
        TILE_SIZE,
        TILE_SIZE,
        margin,
        spacing,
      );
      if (!tileset) {
        console.warn(
          `[MapHandler] Tileset not found: name='${ts.name}', imageKey='${ts.imageKey}'. Check tileset names in map JSON and code.`,
        );
      } else {
        tilesetObjects.push(tileset);
      }
    });

    if (tilesetObjects.length === 0) {
      console.error(
        "[MapHandler] No tilesets were loaded. Layers cannot be created.",
      );
      return;
    }

    if (!this.map || !Array.isArray(this.map.layers)) {
      console.error(
        "[MapHandler] Tilemap is null or map.layers is not an array. Map JSON may be malformed.",
      );
      return;
    }
    // Dynamically create all layers from the map
    this.map.layers.forEach((layerData) => {
      // Skip undefined layers and non-tile layers
      if (!layerData) {
        return;
      }

      // Check if this is a tile layer (has data property) and has a valid name
      if (!layerData.data || typeof layerData.name !== "string") {
        console.warn(
          "[MapHandler] Skipping non-tile layer or invalid layer:",
          layerData?.name || "unnamed",
        );
        return;
      }

      const layerName = layerData.name;
      // createLayer expects tileset array directly
      const layer = this.map?.createLayer(layerName, tilesetObjects, 0, 0);
      if (!layer) {
        console.warn(
          `[MapHandler] Layer could not be created: '${layerName}'. Check layer names in map JSON.`,
        );
      } else {
        this.layers[layerName] = layer;
      }
    });

    // Set layer rendering order (depth) - bottom to top:
    // tiles (0), tile-effects (1), tile-objects-block (2),
    // players/enemies/loot (1000 + Y position for Y-sorting),
    // tile-object-overlay (10000), roofs (10001)
    const tilesLayer = this.getLayer("tiles");
    if (tilesLayer) {
      tilesLayer.setDepth(0);
    }

    const effectLayer = this.getLayer("tile-effects");
    if (effectLayer) {
      effectLayer.setVisible(true);
      effectLayer.setDepth(1);
    }

    const blockLayer = this.getLayer("tile-objects-block");
    if (blockLayer) {
      // Set base depth for the layer
      blockLayer.setDepth(2);
    }

    const interactablesLayer = this.getLayer("interactables");
    if (interactablesLayer) {
      // Set depth same as block layer since interactables are also blocking objects
      interactablesLayer.setDepth(2);
    }

    // Detect loot boxes in 'containers' layer
    const containersLayer = this.getLayer("containers");
    if (containersLayer) {
      containersLayer.forEachTile((tile: Phaser.Tilemaps.Tile) => {
        if (tile.index !== -1 && tile.index !== 0) {
          const lootBox = new LootBox(
            { x: tile.x, y: tile.y, items: [] },
            undefined,
          );
          lootBox.fixed = true;
          lootBox.isChest = tile.index === CHEST_TILE_INDEX;
          this.lootBoxes.push(lootBox);
        }
      });
    }

    const overlayLayer = this.getLayer("tile-object-overlay");
    if (overlayLayer) {
      // Set high depth to render above all dynamic objects (1000 + max Y)
      overlayLayer.setDepth(10000);
    }

    const roofsLayer = this.getLayer("roofs");
    if (roofsLayer) {
      // Set even higher depth to render above overlay
      roofsLayer.setDepth(10001);
    }
  }

  getLayer(name: string): Phaser.Tilemaps.TilemapLayer | undefined {
    return this.layers[name];
  }

  /**
   * Get the tilemap instance for external use (e.g., camera bounds)
   */
  getTilemap(): Phaser.Tilemaps.Tilemap | null {
    return this.map;
  }

  /**
   * Get the width of the map in tiles
   */
  getMapWidthTiles(): number {
    return this.map?.width || MAP_WIDTH_TILES;
  }

  /**
   * Get the height of the map in tiles
   */
  getMapHeightTiles(): number {
    return this.map?.height || MAP_HEIGHT_TILES;
  }

  /**
   * Get the width of the map in pixels
   */
  getMapWidthPixels(): number {
    return this.getMapWidthTiles() * TILE_SIZE;
  }

  /**
   * Get the height of the map in pixels
   */
  getMapHeightPixels(): number {
    return this.getMapHeightTiles() * TILE_SIZE;
  }

  /**
   * Returns the loot box adjacent to the given tile coordinates, or null if none.
   */
  getAdjacentLootBox(tileX: number, tileY: number): LootBox | null {
    for (const lootBox of this.lootBoxes) {
      if (lootBox.isPlayerAdjacent(tileX, tileY)) {
        return lootBox;
      }
    }
    return null;
  }

  /**
   * Check if the given world (pixel) coordinates are on an indoor tile.
   */
  isIndoorTile(x: number, y: number): boolean {
    const tileX = Math.floor(x / TILE_SIZE);
    const tileY = Math.floor(y / TILE_SIZE);

    // Check indoor layer for any tile with a value (non-zero)
    const indoorLayer = this.getLayer("indoor");
    if (indoorLayer) {
      const indoorTile = indoorLayer.getTileAt(tileX, tileY);
      if (indoorTile && indoorTile.index > 0) {
        return true;
      }
    }

    return false;
  }

  /**
   * Check if a single tile is blocked (helper function)
   */
  private isTileBlocked(tileX: number, tileY: number): boolean {
    const blockLayer = this.getLayer("tile-objects-block");
    if (!blockLayer) return false;

    const tile = blockLayer.getTileAt(tileX, tileY);
    if (tile !== null && tile.index !== -1 && tile.index !== 0) {
      return true;
    }

    const containersLayer = this.getLayer("containers");
    if (!containersLayer) return false;

    const containerTile = containersLayer.getTileAt(tileX, tileY);
    if (
      containerTile !== null &&
      containerTile.index !== -1 &&
      containerTile.index !== 0
    ) {
      return true;
    }

    return false;
  }

  /**
   * Checks if the given world (pixel) coordinates are blocked.
   */
  isBlocked(x: number, y: number): boolean {
    const tileX = Math.floor(x / TILE_SIZE);
    const tileY = Math.floor(y / TILE_SIZE);

    return this.isTileBlocked(tileX, tileY);
  }

  public renderTeleportSprites(): void {
    // Remove previous teleport sprites if any
    if (!this.teleportSprites) {
      this.teleportSprites = [];
    } else {
      this.teleportSprites.forEach((sprite: Phaser.GameObjects.Sprite) =>
        sprite.destroy(),
      );
      this.teleportSprites = [];
    }

    // Wait for teleport-active texture before rendering
    if (!this.scene.textures.exists("teleport-active")) {
      this.scene.load.once("complete", () => {
        this.renderTeleportSprites();
      });
      return;
    }

    // Create animation if it doesn't exist
    if (!this.scene.anims.exists("teleport-active-anim")) {
      this.scene.anims.create({
        key: "teleport-active-anim",
        frames: this.scene.anims.generateFrameNumbers("teleport-active", {
          start: 0,
          end: 1,
        }),
        frameRate: 4,
        repeat: -1,
      });
    }

    // Get the tiles-effect layer
    const effectsLayer =
      this.getLayer("tiles-effect") || this.getLayer("tile-effects");
    const effectDepth = effectsLayer ? effectsLayer.depth : 5;

    this.teleportsActive.forEach((tp) => {
      // Convert tile position to pixel position (center of tile)
      const px = tp.x * TILE_SIZE + TILE_SIZE / 2;
      const py = tp.y * TILE_SIZE + TILE_SIZE / 2;
      // Create animated sprite using preloaded 'teleport-active' spritesheet
      const sprite = this.scene.add.sprite(px, py, "teleport-active");
      sprite.play("teleport-active-anim");
      // Set sprite to same depth as tile-effects layer
      sprite.setDepth(effectDepth);

      // Make sure UI camera ignores this sprite (only main camera should render it)
      if ((this.scene as any).uiCamera) {
        (this.scene as any).uiCamera.ignore(sprite);
      }

      this.teleportSprites.push(sprite);
    });
  }

  public renderActiveTeleportSprite(tileX: number, tileY: number): void {
    // Wait for teleport-active texture before rendering
    if (!this.scene.textures.exists("teleport-active")) {
      this.scene.load.once("complete", () => {
        this.renderActiveTeleportSprite(tileX, tileY);
      });
      return;
    }

    // Create animation if it doesn't exist
    if (!this.scene.anims.exists("teleport-active-anim")) {
      this.scene.anims.create({
        key: "teleport-active-anim",
        frames: this.scene.anims.generateFrameNumbers("teleport-active", {
          start: 0,
          end: 1,
        }),
        frameRate: 4,
        repeat: -1,
      });
    }

    // Get the tiles-effect layer
    const effectsLayer =
      this.getLayer("tiles-effect") || this.getLayer("tile-effects");
    const effectDepth = effectsLayer ? effectsLayer.depth : 5;

    // Convert tile position to pixel position (center of tile)
    const TILE_SIZE = 32;
    const px = tileX * TILE_SIZE + TILE_SIZE / 2;
    const py = tileY * TILE_SIZE + TILE_SIZE / 2;

    // Create animated sprite using preloaded 'teleport-active' spritesheet
    const sprite = this.scene.add.sprite(px, py, "teleport-active");
    sprite.play("teleport-active-anim");
    // Set sprite to same depth as tile-effects layer
    sprite.setDepth(effectDepth);

    // Make sure UI camera ignores this sprite (only main camera should render it)
    if ((this.scene as any).uiCamera) {
      (this.scene as any).uiCamera.ignore(sprite);
    }

    // Store as active teleport sprite for potential cleanup
    (this.scene as any).activeTeleportSprite = sprite;
  }

  /**
   * Check roof visibility based on player position
   * Hides/shows roof tiles and fog based on player standing on specific tile types
   */
  public checkRoofVisibility(playerX: number, playerY: number): void {
    const tilesLayer = this.getLayer("tiles");
    const roofsLayer = this.getLayer("roofs");

    if (!tilesLayer || !roofsLayer) return;

    const tileX = Math.floor(playerX / tilesLayer.tilemap.tileWidth);
    const tileY = Math.floor(playerY / tilesLayer.tilemap.tileHeight);

    const seeAllLayer = this.getLayer("see-all");
    const seeAllTile = seeAllLayer?.getTileAt(tileX, tileY);

    // Hide roofs and show fog overlay when player is standing on:
    // - indoor layer with non-zero tile
    // - see-all layer with non-zero value
    const isIndoor = this.isIndoorTile(playerX, playerY);
    const canSeeAll = seeAllTile && seeAllTile.index > 0;

    if (canSeeAll) {
      roofsLayer.setVisible(false);
      this.hideFogOverlay();
    } else if (isIndoor) {
      roofsLayer.setVisible(false);
      this.showFogOverlay();
    } else {
      roofsLayer.setVisible(true);
      this.hideFogOverlay();
    }
  }

  /**
   * Show fog overlay covering all non-indoor tiles
   */
  private showFogOverlay(): void {
    const tilesLayer = this.getLayer("tiles");
    if (!tilesLayer) return;
    const tilesEffectLayer = this.getLayer("tile-effects");
    if (!tilesEffectLayer) return;
    const tilesOverlayLayer = this.getLayer("tile-object-overlay");
    if (!tilesOverlayLayer) return;

    // Create container if it doesn't exist
    if (!this.fogOverlayContainer) {
      this.fogOverlayContainer = this.scene.add.container(0, 0);
      this.fogOverlayContainer.setDepth(15000); // Above all game objects including monsters and overlay layers

      // Ensure fog overlay is ignored by UI camera (rendered by main camera only)
      if ((this.scene as any).uiCamera) {
        (this.scene as any).uiCamera.ignore(this.fogOverlayContainer);
      }
    }

    // Destroy old fog graphics if it exists
    if (this.fogGraphics) {
      this.fogGraphics.destroy();
      this.fogGraphics = undefined;
    }

    // Clear fog tiles array
    this.fogTiles = [];

    const tileWidth = tilesLayer.tilemap.tileWidth;
    const tileHeight = tilesLayer.tilemap.tileHeight;

    // Get camera bounds in tile coordinates
    const camera = this.scene.cameras.main;
    const startX = Math.floor(camera.worldView.x / tileWidth);
    const startY = Math.floor(camera.worldView.y / tileHeight);
    const endX = Math.ceil(
      (camera.worldView.x + camera.worldView.width) / tileWidth,
    );
    const endY = Math.ceil(
      (camera.worldView.y + camera.worldView.height) / tileHeight,
    );

    // Create a single graphics object for all fog tiles to avoid gaps
    this.fogGraphics = this.scene.add.graphics();
    this.fogGraphics.fillStyle(0x000000, 1.0); // Black with 100% opacity

    // Draw all fog tiles in one graphics object
    for (let y = startY; y <= endY; y++) {
      for (let x = startX; x <= endX; x++) {
        // Convert tile coordinates to world coordinates (center of tile)
        const worldX = (x + 0.5) * tileWidth;
        const worldY = (y + 0.5) * tileHeight;

        // Only create fog for tiles that are NOT indoor
        if (!this.isIndoorTile(worldX, worldY)) {
          // Draw rectangle for this tile (no gaps between tiles in same graphics object)
          this.fogGraphics.fillRect(
            x * tileWidth,
            y * tileHeight,
            tileWidth,
            tileHeight,
          );
        }
      }
    }

    this.fogOverlayContainer.add(this.fogGraphics);
    this.fogOverlayContainer.setVisible(true);
  }

  /**
   * Hide fog overlay
   */
  private hideFogOverlay(): void {
    if (this.fogOverlayContainer) {
      this.fogOverlayContainer.setVisible(false);
      // Clear fog graphics when hiding to free up memory
      if (this.fogGraphics) {
        this.fogGraphics.destroy();
        this.fogGraphics = undefined;
      }
    }
  }

  /**
   * Destroy fog overlay resources when cleaning up the scene
   */
  public destroyFogOverlay(): void {
    if (this.fogOverlayContainer) {
      if (this.fogGraphics) {
        this.fogGraphics.destroy();
        this.fogGraphics = undefined;
      }
      this.fogTiles = [];
      this.fogOverlayContainer.destroy();
      this.fogOverlayContainer = undefined;
    }
  }
}
